import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Ticket, MapPin, CalendarDays, Hash, ShieldCheck, Sparkles, Star } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import { joinPassport, lookupPassport } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { SectionHead } from "@/components/SectionHead";

const STORAGE_KEY = "chillwoah-passport";

function PassportCard({ member }) {
  const joined = member.joined_at ? new Date(member.joined_at) : new Date();
  const dateStr = joined.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }).toUpperCase();

  return (
    <motion.div
      data-testid="passport-card"
      initial={{ opacity: 0, rotateY: 12, y: 24 }}
      animate={{ opacity: 1, rotateY: 0, y: 0 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className="relative bg-[#0F0F14] border-2 border-[#F26522]/70 p-6 sm:p-8 overflow-hidden"
      style={{ boxShadow: "0 0 80px rgba(242,101,34,0.18)" }}
    >
      <div className="absolute top-0 left-0 right-0 h-1.5 bg-[#F26522]" aria-hidden="true" />
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-mono text-xs font-semibold tracking-[0.3em] uppercase text-[#F5E6D3]">Chillwoah Official</p>
          <h3 className="font-display font-extrabold uppercase text-3xl sm:text-4xl text-white mt-1">Passport</h3>
        </div>
        <Ticket className="w-10 h-10 text-[#F26522]" aria-hidden="true" />
      </div>

      <div className="mt-8 border-t border-dashed border-[#2A2A36] pt-8">
        <p className="font-mono text-xs tracking-[0.3em] uppercase text-zinc-300">Member Number — Permanent</p>
        <p data-testid="passport-number" className="font-mono font-semibold text-5xl sm:text-6xl text-[#F26522] mt-2 tracking-tight">
          #{member.passport_id}
        </p>
        {member.founding && (
          <p className="mt-3 inline-flex items-center gap-2 bg-[#F26522] text-black font-display font-extrabold uppercase tracking-widest text-xs px-4 py-2">
            <Star className="w-4 h-4" aria-hidden="true" /> Founding Member — First 1,000
          </p>
        )}
      </div>

      <dl className="mt-8 grid grid-cols-2 gap-6">
        <div>
          <dt className="font-mono text-[11px] tracking-[0.25em] uppercase text-zinc-300 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-[#F26522]" aria-hidden="true" /> City
          </dt>
          <dd className="font-display font-bold uppercase text-lg text-white mt-1">{member.city || "Brooklyn, NY"}</dd>
        </div>
        <div>
          <dt className="font-mono text-[11px] tracking-[0.25em] uppercase text-zinc-300 flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-[#F26522]" aria-hidden="true" /> Joined
          </dt>
          <dd className="font-display font-bold uppercase text-lg text-white mt-1">{dateStr}</dd>
        </div>
      </dl>

      <p className="mt-8 font-editorial italic text-lg text-[#F5E6D3]">
        Hold this number. It is yours forever — early drops, exclusive songs and members-only experiences travel with it.
      </p>
    </motion.div>
  );
}

/** The CHILLWOAH Passport — real numbered membership, first 1,000 free. */
export default function PassportSection({ teaser = false }) {
  const { content } = useContent();
  const [member, setMember] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", sms_opt_in: false });
  const [lookupEmail, setLookupEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
      if (saved?.passport_id) setMember(saved);
    } catch {
      /* fresh start */
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const res = await joinPassport(form);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(res.member));
      setMember(res.member);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not register — check the email address and try again.");
    } finally {
      setBusy(false);
    }
  };

  const lookup = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const res = await lookupPassport(lookupEmail);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(res.member));
      setMember(res.member);
    } catch (err) {
      setError(err?.response?.data?.detail || "No passport found for that email.");
    } finally {
      setBusy(false);
    }
  };

  const bg = content?.images?.passport_bg;

  return (
    <section className="relative overflow-hidden" aria-labelledby="passport-heading">
      {bg && (
        <div className="absolute inset-0" aria-hidden="true">
          <img src={bg} alt="" loading="lazy" className="w-full h-full object-cover opacity-40 saturate-[0.8]" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#08080A] via-[#08080A]/70 to-[#08080A]" />
        </div>
      )}

      <div className={`relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${teaser ? "py-14 sm:py-20" : "py-16 sm:py-24 lg:py-32"}`}>
        {!teaser ? (
          <SectionHead eyebrow="Membership" title="The Chillwoah Passport" index="/ 04" className="relative" />
        ) : null}

        <div className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-start ${teaser ? "" : "mt-4"}`}>
          <Reveal className="lg:col-span-6">
            <span id="passport-heading" className="block font-mono text-xs font-semibold tracking-[0.3em] uppercase text-[#F26522]">
              {teaser ? "Membership" : "Collectible. Numbered. Permanent."}
            </span>
            <h2 className="mt-3 font-display font-extrabold uppercase text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.05]">
              {teaser ? (
                <>Get your number.<br />Keep it forever.</>
              ) : (
                <>One number.<br />The whole world.</>
              )}
            </h2>
            <p className="mt-4 text-lg text-zinc-300 max-w-xl">
              {teaser
                ? "A collectible, numbered membership — not a mailing list. The first 1,000 numbers are free, and yours is permanent."
                : "The CHILLWOAH Passport is a collectible membership, not a mailing list. The first 1,000 numbers are free — and once yours, it never resets."}
            </p>

            <ul className={`flex flex-col ${teaser ? "mt-5 gap-3" : "mt-8 gap-4"}`}>
              {(teaser
                ? [
                    { icon: Ticket, text: "Early access to XRABI drops" },
                    { icon: Sparkles, text: "Exclusive songs for members" },
                    { icon: MapPin, text: "Members-only events and secret sessions" },
                  ]
                : [
                    { icon: Sparkles, text: "Exclusive songs and unreleased material for members" },
                    { icon: Ticket, text: "Early access to XRABI drops before anyone else" },
                    { icon: MapPin, text: "Members-only events and secret Brooklyn sessions" },
                    { icon: ShieldCheck, text: "Points, rewards and future digital collectibles — optional, never required" },
                  ]
              ).map((b, i) => (
                <li
                  key={i}
                  className={`flex items-center border border-[#1A1A22] bg-[#0F0F14]/80 text-white ${teaser ? "gap-3 px-4 py-3" : "gap-4 px-5 py-4"}`}
                >
                  <b.icon className="w-6 h-6 text-[#F26522] shrink-0" aria-hidden="true" />
                  <span className="text-base">{b.text}</span>
                </li>
              ))}
            </ul>

            {!teaser && (
              <div className="mt-8 border border-[#1A1A22] bg-[#0F0F14]/80 p-6">
                <p className="font-mono text-xs font-semibold tracking-[0.3em] uppercase text-[#F26522] mb-3 flex items-center gap-2">
                  <Hash className="w-4 h-4" aria-hidden="true" /> Passport rules
                </p>
                <ul className="list-disc list-inside text-zinc-300 space-y-2 text-base">
                  <li>One Passport per email address — numbered in order of joining, starting at #CW-0001.</li>
                  <li>The first 1,000 Passports are free and carry Founding Member status.</li>
                  <li>Numbers are permanent and non-transferable.</li>
                  <li>Points and reward levels arrive later — your number qualifies from day one.</li>
                  <li>Future wallet-connected collectibles are optional; no crypto needed to join or shop.</li>
                </ul>
              </div>
            )}
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-6">
            {member ? (
              <div className="flex flex-col gap-6">
                <PassportCard member={member} />
                <p data-testid="passport-welcome" className="text-center font-mono text-sm tracking-[0.25em] uppercase text-zinc-300">
                  Welcome to the world, #{member.passport_id}
                </p>
              </div>
            ) : (
              <form
                data-testid="passport-form"
                onSubmit={submit}
                className="relative bg-[#0F0F14] border border-[#2A2A36] p-6 sm:p-8 flex flex-col gap-5"
              >
                <p className="font-display font-extrabold uppercase text-2xl text-white">Claim your number</p>
                <p className="text-zinc-300 -mt-2">Under a minute. Founding numbers are going in order.</p>

                <div>
                  <label htmlFor="pp-name" className="block font-mono text-xs tracking-[0.25em] uppercase text-zinc-300 mb-2">
                    Name <span className="text-zinc-400">(optional)</span>
                  </label>
                  <input
                    id="pp-name"
                    data-testid="passport-name-input"
                    className="lux-input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    autoComplete="name"
                  />
                </div>
                <div>
                  <label htmlFor="pp-email" className="block font-mono text-xs tracking-[0.25em] uppercase text-zinc-300 mb-2">
                    Email <span aria-hidden="true" className="text-[#F26522]">*</span>
                  </label>
                  <input
                    id="pp-email"
                    data-testid="passport-email-input"
                    type="email"
                    required
                    className="lux-input"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    autoComplete="email"
                    placeholder="you@example.com"
                  />
                </div>
                <div>
                  <label htmlFor="pp-phone" className="block font-mono text-xs tracking-[0.25em] uppercase text-zinc-300 mb-2">
                    Mobile for SMS alerts <span className="text-zinc-400">(optional)</span>
                  </label>
                  <input
                    id="pp-phone"
                    data-testid="passport-phone-input"
                    type="tel"
                    className="lux-input"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    autoComplete="tel"
                    placeholder="+1 ___ ___ ____"
                  />
                  <label className="mt-3 flex items-center gap-3 text-sm text-zinc-300 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="passport-sms-optin"
                      checked={form.sms_opt_in}
                      onChange={(e) => setForm({ ...form, sms_opt_in: e.target.checked })}
                      className="w-5 h-5 accent-[#F26522]"
                    />
                    Send me drop + event alerts by SMS
                  </label>
                </div>
                <div>
                  <label htmlFor="pp-city" className="block font-mono text-xs tracking-[0.25em] uppercase text-zinc-300 mb-2">
                    City / Borough <span className="text-zinc-400">(optional)</span>
                  </label>
                  <input
                    id="pp-city"
                    data-testid="passport-city-input"
                    className="lux-input"
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    placeholder="Brooklyn"
                  />
                </div>

                {error && (
                  <p data-testid="passport-error" role="alert" className="text-[#FF7733] font-semibold text-base">
                    {error}
                  </p>
                )}

                <button
                  data-testid="passport-submit"
                  type="submit"
                  disabled={busy}
                  className="h-16 bg-[#F26522] hover:bg-[#FF7733] disabled:opacity-60 text-black font-display font-extrabold uppercase tracking-widest text-lg transition-all hover:-translate-y-0.5 active:scale-95"
                >
                  {busy ? "Stamping..." : "Issue My Passport"}
                </button>
              </form>
            )}

            {!member && (
              <form onSubmit={lookup} className="mt-6 flex flex-col sm:flex-row gap-3 items-stretch">
                <label htmlFor="pp-lookup" className="sr-only">
                  Email to look up an existing passport
                </label>
                <input
                  id="pp-lookup"
                  data-testid="passport-lookup-input"
                  type="email"
                  required
                  className="lux-input flex-1"
                  value={lookupEmail}
                  onChange={(e) => setLookupEmail(e.target.value)}
                  placeholder="Already a member? Enter your email"
                />
                <button
                  data-testid="passport-lookup-button"
                  type="submit"
                  disabled={busy}
                  className="h-14 px-8 border-2 border-[#2A2A36] hover:border-[#F26522] text-white font-display font-bold uppercase tracking-widest text-sm transition-colors"
                >
                  Find My Passport
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
}
